import { useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useThemeStore } from '@/store/themeStore';
import {
  SEMANTIC_TOKEN_MAP,
  DARK_SEMANTIC_TOKEN_MAP,
  LEGACY_ALIAS_MAP,
  TAILWIND_COLOR_MAP,
  DARK_TAILWIND_COLOR_MAP,
} from '@/config/themeTokens';

function hexToHsl(hex: string): string | null {
  const cleanHex = hex.replace(/^#/, '');
  if (cleanHex.length < 6) return null;

  const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
  const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
  const b = parseInt(cleanHex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function shiftToRed(hsl: string): string {
  const parts = hsl.split(' ');
  if (parts.length < 3) return hsl;
  return `0 ${parts[1]} ${parts[2]}`;
}

export function ThemeInitializer({ children }: { children: React.ReactNode }) {
  const { theme, fetchTheme } = useThemeStore();
  const hasFetched = useRef(false);
  const [fontsLoaded, setFontsLoaded] = useState(
    () =>
      typeof document !== 'undefined' &&
      !!document.fonts?.check('24px "Material Symbols Rounded"'),
  );

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      void fetchTheme();
    }
  }, [fetchTheme]);

  // Wait for Material Symbols font to be loaded
  useEffect(() => {
    if (fontsLoaded) return;

    let cancelled = false;

    if (document.fonts) {
      document.fonts
        .load('24px "Material Symbols Rounded"')
        .then(() => {
          if (!cancelled) setFontsLoaded(true);
        })
        .catch(() => {
          // Font load failed (CDN error) — timeout fallback will handle it
        });
    }

    // Safety timeout — don't block forever if CDN is slow
    const timeout = setTimeout(() => {
      if (!cancelled) setFontsLoaded(true);
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [fontsLoaded]);

  useEffect(() => {
    if (!theme) return;

    let cssContent = '';

    // Light mode
    cssContent += ':root {\n';

    if (theme.customColors) {
      Object.entries(theme.customColors).forEach(([key, value]) => {
        cssContent += `  --custom-${key}: ${value};\n`;
      });

      Object.entries(SEMANTIC_TOKEN_MAP).forEach(([semanticKey, baseToken]) => {
        const baseColor = theme.customColors![baseToken];
        if (baseColor) {
          cssContent += `  --custom-${semanticKey}: ${baseColor};\n`;
        }
      });

      Object.entries(LEGACY_ALIAS_MAP).forEach(([legacyKey, semanticKey]) => {
        const baseToken = SEMANTIC_TOKEN_MAP[semanticKey];
        const baseColor = baseToken
          ? theme.customColors![baseToken]
          : theme.customColors![semanticKey as keyof typeof theme.customColors];
        if (baseColor) {
          cssContent += `  --custom-${legacyKey}: ${baseColor};\n`;
        }
      });

      const footerBg = theme.customColors['primary-900'];
      if (footerBg) {
        cssContent += `  --custom-landing-footer-bg: ${footerBg};\n`;
      }

      Object.entries(TAILWIND_COLOR_MAP).forEach(([twVar, apiToken]) => {
        const hexColor = theme.customColors![apiToken];
        if (hexColor) {
          const hsl = hexToHsl(hexColor);
          if (hsl) {
            cssContent += `  --${twVar}: ${hsl};\n`;
          }
        }
      });

      // Destructive: derived from secondary-700 shifted to red hue
      const destructiveBase = theme.customColors['secondary-700'];
      if (destructiveBase) {
        const hsl = hexToHsl(destructiveBase);
        if (hsl) {
          cssContent += `  --destructive: ${shiftToRed(hsl)};\n`;
        }
      }
      const destructiveFg = theme.customColors['neutral-0'];
      if (destructiveFg) {
        const hsl = hexToHsl(destructiveFg);
        if (hsl) {
          cssContent += `  --destructive-foreground: ${hsl};\n`;
        }
      }
    }
    cssContent += '}\n\n';

    // Dark mode
    cssContent += '.dark {\n';

    if (theme.customDarkColors) {
      Object.entries(theme.customDarkColors).forEach(([key, value]) => {
        cssContent += `  --custom-${key}: ${value};\n`;
      });

      Object.entries(DARK_SEMANTIC_TOKEN_MAP).forEach(
        ([semanticKey, baseToken]) => {
          const baseColor = theme.customDarkColors![baseToken];
          if (baseColor) {
            cssContent += `  --custom-${semanticKey}: ${baseColor};\n`;
          }
        },
      );

      Object.entries(LEGACY_ALIAS_MAP).forEach(([legacyKey, semanticKey]) => {
        const baseToken = DARK_SEMANTIC_TOKEN_MAP[semanticKey];
        const baseColor = baseToken
          ? theme.customDarkColors![baseToken]
          : theme.customDarkColors![
              semanticKey as keyof typeof theme.customDarkColors
            ];
        if (baseColor) {
          cssContent += `  --custom-${legacyKey}: ${baseColor};\n`;
        }
      });

      const darkFooterBg = theme.customDarkColors['primary-900'];
      if (darkFooterBg) {
        cssContent += `  --custom-landing-footer-bg: ${darkFooterBg};\n`;
      }

      Object.entries(DARK_TAILWIND_COLOR_MAP).forEach(([twVar, apiToken]) => {
        const hexColor = theme.customDarkColors![apiToken];
        if (hexColor) {
          const hsl = hexToHsl(hexColor);
          if (hsl) {
            cssContent += `  --${twVar}: ${hsl};\n`;
          }
        }
      });

      // Destructive dark: derived from secondary-500 shifted to red hue
      const darkDestructiveBase = theme.customDarkColors['secondary-500'];
      if (darkDestructiveBase) {
        const hsl = hexToHsl(darkDestructiveBase);
        if (hsl) {
          cssContent += `  --destructive: ${shiftToRed(hsl)};\n`;
        }
      }
      const darkDestructiveFg = theme.customDarkColors['neutral-0'];
      if (darkDestructiveFg) {
        const hsl = hexToHsl(darkDestructiveFg);
        if (hsl) {
          cssContent += `  --destructive-foreground: ${hsl};\n`;
        }
      }
    }
    cssContent += '}\n';

    let styleTag = document.getElementById('dynamic-custom-theme');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'dynamic-custom-theme';
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = cssContent;

    if (theme.title) {
      document.title = theme.title;
    }

    if (theme.images.favicon) {
      let link: HTMLLinkElement | null =
        document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = theme.images.favicon;
    }
  }, [theme]);

  if (!theme || !fontsLoaded) {
    return (
      <LoadingSpinner
        fullPage
        size="md"
      />
    );
  }

  return children;
}
